import React, { BaseSyntheticEvent, ChangeEvent, useRef, useState } from 'react';
import { useGet, useMutate } from 'restful-react';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { BaseModal } from '../BaseModal/BaseModal';
import './GlossaryModal.scss';
import { Button } from '../../FormComponents/Buttons/Button';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import useProject from '../../../hooks/useProject';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { captionLanguages } from '../../../common/constants';
import { closedCaptionLanguages } from '../../../common/constants';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { Stream } from '../../../types/Stream';
import { OkResponse, Option } from '../../../common/interfaces';
import { ClassToken, DoNotTranslate, Glossaries, Glossary, Replace, Format } from '../../../types/Glossary';
import { GlossaryEditor } from './GlossaryEditor/GlossaryEditor';
import Help from '../../Help/Help';
import ToggleButton from '../../ProducerComponents/ProducerControlPanel/Widgets/ToggleButton';

type Props = {
  isOpen: boolean;
  contentLabel: string;
  selectedGlossary: Glossary;
  handleCloseModal: () => void;
};

type ValidateResult = boolean | string;
type Validate = (value: any) => ValidateResult | Promise<ValidateResult>;

export type CreateGlossaryFormData = {
  id: string;
  modelId: string;
  languageIsoCode: string;
  name: string;
  replace: string;
  format: string;
  doNotTranslate: string;
  tokens: string;
  profanity: boolean;
  userId: string;
  streamId: string;
};

type DeleteGlossaryFormData = {
  glossaryId: string;
  streamId: string;
};

type RemoveGlossaryFormData = {
  glossaryId: string;
  streamId: string;
};

type AddGlossaryFormData = {
  glossaryId: string;
  streamId: string;
};

export const GlossaryModal = (props: Props) => {
  const { t } = useTranslation();
  const { project, setProject } = useProject();

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isDirty, setIsDirty] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [replace, setReplace] = useState<Replace[]>(JSON.parse(props.selectedGlossary.replace));
  const [format, setFormat] = useState<Format[]>(JSON.parse(props.selectedGlossary.format));
  const [doNotTranslate, setDoNotTranslate] = useState<DoNotTranslate[]>(JSON.parse(props.selectedGlossary.doNotTranslate));
  const [tokens, setTokens] = useState<ClassToken[]>(JSON.parse(props.selectedGlossary.tokens));
  const [profanity, setProfanity] = useState<boolean>(props.selectedGlossary.profanity);
  const [glossaries, setGlossaries] = useState<Glossaries>([]);
  const [selectedId, setSelectedId] = useState<string>('00000000-0000-0000-0000-000000000000');

  const isJsonObject = (value: string): boolean => {
    if (typeof value !== "string")
    {
      return false;
    }
    try {
      const parsed = JSON.parse(value);
      return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;

      if (isJsonObject(result as string ?? '')) {

      }
      else {
        const lines = (result as string).split(/\r?\n/);

        let data = [{}];
        let key = 'phrase';

        console.log(activeIndex);

        switch(activeIndex) {
          case 0:
            data = [...format];
            key = 'phrase';
            break;
          case 1:
            data = [...doNotTranslate];
            key = 'phrase';
            break;
          case 2:
            data = [...tokens];
            key = 'phrase';
            break;
          case 3:
            data = [...replace];
            key = 'find';
            break;
        }

        const newData = [...data];
        const newItem = newData.pop();

        for(let i = 0; i < lines.length; i++) {
          const line = lines[i];

          if (!line || line.trim() === '') {
            continue;
          }

          let exists = data.some(x => (x as any)[key] === line);;

          if (!exists) {
            const item = { ...newItem };

            for (const itemKey in item) {
              if (Object.prototype.hasOwnProperty.call(item, itemKey)) {
                (item as any)[itemKey] = '';
              }
            }

            (item as any)[key] = line;

            newData.push(item as any);
          }
        }

        newData.push(newItem as any);

        switch(activeIndex) {
          case 0:
            setFormat(newData as any);
            break;
          case 1:
            setDoNotTranslate(newData as any);
            break;
          case 2:
            setTokens(newData as any);
            break;
          case 3:
            setReplace(newData as any);
            break;
        }
      }
    };

    reader.readAsText(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const getAvailableLanguageIso = (): string => {
    if (props.selectedGlossary.id !== '') {
      return props.selectedGlossary.languageIsoCode;
    }
    
    const usedLanguages = new Set(
      project.stream.glossaries?.map(g => g.languageIsoCode.toLowerCase()) || []
    );

    const topLanguages = [
      "en-US",
      "es-ES",
      "fr-FR",
      "de-DE",
      "zh-CN",
      "ja-JP",
      "ru-RU",
      "pt-BR",
      "it-IT",
      "ko-KR",
    ];

    if (!usedLanguages.has("en-us")) {
      return "en-us";
    }

    for (const lang of topLanguages) {
      if (!usedLanguages.has(lang.toLowerCase())) {
        return lang.toLowerCase();
      }
    }

    const allLanguages = [...captionLanguages, ...closedCaptionLanguages];
    const unused = allLanguages.find(l => !usedLanguages.has(l.value.toLowerCase()));

    return unused?.value || "en-us";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: response, loading, error, refetch } = useGet<Glossaries>({
    path: `glossaries/user/${project.createdByUser?.id}`,
    resolve: (response: OkResponse<Glossaries>) => {
      const result = response.result;
      setGlossaries(result);
      return result;
    },
  });

  const defaultValues: CreateGlossaryFormData = {
    id: '',
    modelId: props.selectedGlossary.modelId,
    name: props.selectedGlossary.name,
    languageIsoCode: getAvailableLanguageIso(),
    replace: props.selectedGlossary.replace,
    format: props.selectedGlossary.format,
    doNotTranslate: props.selectedGlossary.doNotTranslate,
    tokens: props.selectedGlossary.tokens,
    profanity: props.selectedGlossary.profanity,
    userId: project.createdByUser?.id ?? '',
    streamId: project.stream?.id ?? '',
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { control, errors, setValue, getValues, handleSubmit } = useForm<CreateGlossaryFormData>({
    defaultValues,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulCreate = (body: any, data: any) => {
    if (data.statusCode === 200) {

      const newProject = {
        ...project,
        stream: data.result as Stream,
      };

      setProject(newProject);

      props.handleCloseModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulDelete = (body: any, data: any) => {
    if (data.statusCode === 200) {
      if (project) {        
        const newProject = {
          ...project,
          stream: data.result as Stream,
        };

        setProject(newProject);
      }

      props.handleCloseModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulUpdate = (body: any, data: any) => {
    if (data.statusCode === 200) {
      if (project) {        
        const newProject = {
          ...project,
          stream: data.result as Stream,
        };

        setProject(newProject);
      }

      props.handleCloseModal();
    }
  };

   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulAdd = (body: any, data: any) => {
    if (data.statusCode === 200) {
      if (project) {        
        const newProject = {
          ...project,
          stream: data.result as Stream,
        };

        setProject(newProject);
      }

      props.handleCloseModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulRemove = (body: any, data: any) => {
    if (data.statusCode === 200) {
      if (project) {        
        const newProject = {
          ...project,
          stream: data.result as Stream,
        };

        setProject(newProject);
      }

      props.handleCloseModal();
    }
  };

  const {
    mutate: createGlossary,
    loading: loadingCreate,
    error: errorCreate,
  } = useMutate<CreateGlossaryFormData>({
    verb: 'PUT',
    path: '/glossaries/stream',
    onMutate: onSuccessfulCreate,
  });

  const {
    mutate: deleteGlossary,
    loading: loadingDelete,
    error: errorDelete,
  } = useMutate<DeleteGlossaryFormData>({
    verb: 'DELETE',
    path: '/glossaries/stream',
    onMutate: onSuccessfulDelete,
  });

  const {
    mutate: updateGlossary,
    loading: loadingUpdate,
    error: errorUpdate,
  } = useMutate<CreateGlossaryFormData>({
    verb: 'POST',
    path: '/glossaries/stream',
    onMutate: onSuccessfulUpdate,
  });

    const {
    mutate: addGlossary,
    loading: loadingAdd,
    error: errorAdd
  } = useMutate<AddGlossaryFormData>({
    verb: 'POST',
    path: `/glossaries/stream/${project.stream.id ?? ''}`,
    onMutate: onSuccessfulAdd,
  });

  const {
    mutate: removeGlossary,
    loading: loadingRemove,
    error: errorRemove,
  } = useMutate<RemoveGlossaryFormData>({
    verb: 'DELETE',
    path: '/glossaries/stream',
    onMutate: onSuccessfulRemove,
  });

  type AnyObject = Record<string, any>;

  const removeDuplicatesByKey = <T extends AnyObject>(arr: T[], key: keyof T): T[] => {
    const seen = new Set<any>();
    return arr.filter(item => {
      const value = item[key];
      if (seen.has(value)) {
        return false;
      } else {
        seen.add(value);
        return true;
      }
    });
  };

  const onUpdate = (glossaryId: string) => {
    let request = {...getValues() };
    request.id = glossaryId;
    request.userId = project.createdByUser?.id ?? '';
    request.streamId = project.stream?.id ?? '';

    if (format.length > 0 && format[format.length - 1].phrase === '') {
      format.pop();
    }

    if (doNotTranslate.length > 0 && doNotTranslate[doNotTranslate.length - 1].phrase === '') {
      doNotTranslate.pop();
    }

    if (tokens.length > 0 && tokens[tokens.length - 1].phrase === '') {
      tokens.pop();
    }

    if (replace.length > 0 && replace[replace.length - 1].find === '') {
      replace.pop();
    }

    request.format = JSON.stringify(removeDuplicatesByKey(format, 'phrase'));
    request.doNotTranslate = JSON.stringify(removeDuplicatesByKey(doNotTranslate, 'phrase'));
    request.tokens = JSON.stringify(removeDuplicatesByKey(tokens, 'phrase'));
    request.replace = JSON.stringify(removeDuplicatesByKey(replace, 'find'));
    request.profanity = profanity;

    updateGlossary(request);
  }

  const onAttach = (glossaryId: string) => {
    let request = {
      glossaryId: glossaryId,
      streamId: project.stream.id ?? ''
    };

    addGlossary(request);
  }

  const onRemove = (glossaryId: string) => {
    let request = {} as RemoveGlossaryFormData;
    request.glossaryId = glossaryId;
    request.streamId = project.stream?.id ?? '';

    removeGlossary(request);
  }

  const onSubmit: SubmitHandler<CreateGlossaryFormData> = (
    data: CreateGlossaryFormData,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault();
    let request = { ...data };
    request.userId = project.createdByUser?.id ?? '';
    request.streamId = project.stream?.id ?? '';

    if (format.length > 0 && format[format.length - 1].phrase === '') {
      format.pop();
    }

    if (doNotTranslate.length > 0 && doNotTranslate[doNotTranslate.length - 1].phrase === '') {
      doNotTranslate.pop();
    }

    if (tokens.length > 0 && tokens[tokens.length - 1].phrase === '') {
      tokens.pop();
    }

    if (replace.length > 0 && replace[replace.length - 1].find === '') {
      replace.pop();
    }

    request.format = JSON.stringify(removeDuplicatesByKey(format, 'phrase'));
    request.doNotTranslate = JSON.stringify(removeDuplicatesByKey(doNotTranslate, 'phrase'));
    request.tokens = JSON.stringify(removeDuplicatesByKey(tokens, 'phrase'));
    request.replace = JSON.stringify(removeDuplicatesByKey(replace, 'find'));
    request.profanity = profanity;

    createGlossary(request);
  };

  const onDelete = (data: DeleteGlossaryFormData) => {
    deleteGlossary(data);
  };

  const getLanguages = (all: boolean): Option[] => {
    const allLanguages = [...captionLanguages, ...closedCaptionLanguages];

    if (all) {
      return allLanguages;
    }

    const usedLanguages = new Set(
      project.stream.glossaries?.map(g => g.languageIsoCode.toLowerCase()) || []
    );

    return allLanguages.filter(
      lang => !usedLanguages.has(lang.value.toLowerCase())
    );
  };

  const getErrorMessageKey = (value: string) => {
      if (!value
        || value === '') {
        return "producer.control-panel.glossaries.name-missing-value";
      }

      if (value.length > 150) {
          return "producer.control-panel.glossaries.name-max-length";
      }

      const exists = glossaries.some(
        (g) =>
          g.name.trim().toLowerCase() === value.trim().toLowerCase() &&
          g.id !== props.selectedGlossary.id
      );

      if (exists) {
        return "producer.control-panel.glossaries.name-duplicate";
      }
    };

  const getGlossaries = (): Option[] => {
    const options: Option[] = [
      { text: "New Glossary", value: "00000000-0000-0000-0000-000000000000" },
    ];

    const languages = getLanguages(props.selectedGlossary.id !== '');
    const addedGlossaryIds = new Set(project.stream.glossaries?.map(g => g.id) || []);
    const addedLanguageCodes = new Set(project.stream.glossaries?.map(g => g.languageIsoCode.toLowerCase()) || []);

    const filtered = glossaries
      .filter(g => 
        !addedGlossaryIds.has(g.id) &&
        !addedLanguageCodes.has(g.languageIsoCode.toLowerCase())
      )
      .sort((a, b) => {
        if (a.languageIsoCode < b.languageIsoCode) {
          return -1;
        }

        if (a.languageIsoCode > b.languageIsoCode) {
          return 1;
        }

        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }

        return 0;
      });

    for (const g of filtered) {
      const isoName = languages.find(l => l.value.toLowerCase() === g.languageIsoCode.toLowerCase())?.text;
      const name = `[${isoName}] ${g.name}`;
      options.push({ text: name, value: g.id });
    }

    return options;
  };


  if (errorCreate || errorDelete || errorRemove || errorUpdate || errorAdd) {
    return <ErrorMessage message={t('error-messages.generic-error')} />;
  }

  return (
    <BaseModal
      isOpen={props.isOpen}
      contentLabel={props.contentLabel}
      handleCloseModal={props.handleCloseModal}
      ariaHideApp={true}
      isDirty={isDirty}
      title="producer.control-panel.glossaries.modal-title"
    >
      <form className="glossaries-modal" onSubmit={handleSubmit(onSubmit)}>
        {getGlossaries().length > 1 && props.selectedGlossary.id === '' ? (
          <>
            <label>{t('producer.control-panel.glossaries.all-glossaries-title')}</label>
            
            <div style={{maxWidth: 270}}>
              <Dropdown
                value={selectedId || ''}
                options={getGlossaries()}
                onChange={(data) => {
                  setSelectedId(data?.value ?? '');
                }}
                placeholderKey="producer.control-panel.glossaries.all-glossaries-placeholder"
                errorType={errors.languageIsoCode}
                errorMessageKey="producer.control-panel.glossaries.all-glossaries-missing-value"
                readonly={
                  props.selectedGlossary.id !== ''
                }
              />
            </div>
          </>
        ) : null}

        {selectedId === '00000000-0000-0000-0000-000000000000' && (
          <>
            <div className="group">
              <div className="column">
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: true,
                    maxLength: 150,
                    validate: (value: any): boolean | string => {
                      if (!value) {
                        return "producer.control-panel.glossaries.name-missing-value";
                      }

                      if (value.length > 150) {
                        return "producer.control-panel.glossaries.name-max-length";
                      }

                      const exists = glossaries.some(
                        (g) =>
                          g.name.trim().toLowerCase() === value.trim().toLowerCase() &&
                          g.id !== props.selectedGlossary.id
                      );

                      if (exists) {
                        return "producer.control-panel.glossaries.name-duplicate";
                      }

                      return true;
                    }
                  }}
                  render={({ onChange, value }) => (
                    <>
                      <label>{t('producer.control-panel.glossaries.name-title')}</label>
                      <div style={{maxWidth: 270}}>
                        <TextInput
                          placeholder="producer.control-panel.glossaries.name-placeholder"
                          value={value}
                          onChange={onChange}
                          onAfterChange={null}
                          errorType={errors.name}
                          errorMessageKey={getErrorMessageKey(value)}
                          readOnly={false}
                        />
                      </div>
                    </>
                  )}
                />
              </div>
              
              <div className="column">
                <Controller
                  name="languageIsoCode"
                  control={control}
                  render={({ onChange, value }) => (
                    <>
                      <label>{t('producer.control-panel.glossaries.language-title')}</label>

                      <div style={{maxWidth: 200}}>
                        <Dropdown
                          value={value || null}
                          options={getLanguages(props.selectedGlossary.id !== '')}
                          onChange={(data) => onChange(data?.value)}
                          placeholderKey="producer.control-panel.glossaries.language-placeholder"
                          errorType={errors.languageIsoCode}
                          errorMessageKey="producer.control-panel.glossaries.language-missing-value"
                          readonly={
                            props.selectedGlossary.id !== ''
                          }
                        />
                      </div>
                      
                    </>
                  )}
                />
              </div>

              <div className="column">
                <Controller
                  name="profanity"
                  control={control}
                  render={({ onChange, value }) => (
                    <>
                      <label>{t('producer.control-panel.glossaries.profanity-title')}
                        <Help
                          title="🛡️ Profanity Filter"
                          text="<br/>When enabled, the profanity filter automatically detects and masks offensive or inappropriate words in transcribed text. This helps ensure that your application’s output remains clean and suitable for all audiences.<br/><br/>You can choose to replace profanities (e.g., ****).<br/><br/>The filter works in multiple languages and can be adjusted per user or per project.<br/><br/><b>Custom Filtering</b><br/>If a particular word or phrase is not included in the built-in profanity list, you can use the Find and Replace functionality to handle it manually. This allows you to extend or fine-tune the filter for specific use cases or domains.<br/><br/><b>Example:</b> If the word “Hello” is not included in the list, you can set the filter to find “Hello” and replace it with “****”. In this example, any instance of “Hello” will be replaced with stars during processing.<br/><br/><b>Supported Languages</b><br/>The system supports profanity detection in multiple languages, including English, Spanish, French, German, Italian, Portuguese, Dutch, Russian, Turkish, Arabic, Hindi, Japanese, Korean, and Chinese. Language coverage may vary depending on vocabulary size and dialects."
                        />
                      </label>
                      <ToggleButton
                        leftText={t('producer.common.on')}
                        leftValue={true}
                        leftButtonClassName={`profanity-button ${
                          value === true
                            ? 'control-panel-button-selected-button'
                            : null
                        }`}
                        leftContentClassName="profanity-text"
                        rightText={t('producer.common.off')}
                        rightValue={false}
                        rightButtonClassName={`profanity-button ${
                          value === false
                            ? 'control-panel-button-selected-button'
                            : null
                        }`}
                        rightContentClassName="profanity-text"
                        footerText={''}
                        isReadOnly={false}
                        onSelection={(value) => {
                          onChange(value);
                          setProfanity(value as boolean);
                        }}
                        leftAccessibilityName={t('producer.common.on')}
                        rightAccessibilityName={t('producer.common.off')}
                      />
                    </>
                  )}
                />
              </div>
            </div>

            <GlossaryEditor
              format={format}
              setFormat={setFormat}
              doNotTranslate={doNotTranslate}
              setDoNotTranslate={setDoNotTranslate}
              tokens={tokens}
              setTokens={setTokens}
              replace={replace}
              setReplace={setReplace}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              setIsDirty={setIsDirty}
            ></GlossaryEditor>
          </>
        )}

        <div className="button-group">
          {props.selectedGlossary.id === '' && selectedId === '00000000-0000-0000-0000-000000000000' ? (
            <>
              {!loadingCreate ? (
                <>
                  <Button
                    translationKey="producer.control-panel.glossaries.set"
                    variant="primary"
                    color="blue"
                    onClick={() => null}
                    type="submit"
                    disabled={loadingCreate}
                  />
                  <input
                    type="file"
                    accept=".txt,.json,.html"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-primary--pink"
                    style={{ alignSelf: "center !important" }}
                    onClick={handleButtonClick}
                  >Import</button>
                </>
              ) : (
                <LoadingSpinner />
              )}
            </>
          ) : null}
          {props.selectedGlossary.id === '' && selectedId !== '00000000-0000-0000-0000-000000000000' ? (
          <>
            {!loadingAdd ? (
              <Button
                translationKey="producer.control-panel.glossaries.set"
                variant="primary"
                color="blue"
                onClick={() => onAttach(selectedId)}
                type="button"
                disabled={loadingAdd}
              />
            ) : (
              <LoadingSpinner />
            )}
          </>
        ) : null}
          {props.selectedGlossary.id !== '' ? (
          <>
            {!loadingUpdate ? (
              <>
                <Button
                  translationKey="producer.control-panel.glossaries.set"
                  variant="primary"
                  color="blue"
                  onClick={() => onUpdate(props.selectedGlossary.id)}
                  type="button"
                  disabled={loadingUpdate}
                />
                <input
                    type="file"
                    accept=".txt,.json,.html"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-primary--pink"
                    style={{ alignSelf: "center !important" }}
                    onClick={handleButtonClick}
                  >Import</button>
                </>
            ) : (
              <LoadingSpinner />
            )}
            {!loadingRemove ? (
              <Button
                translationKey="producer.control-panel.glossaries.remove"
                variant="primary"
                color="pink"
                onClick={() => onRemove(props.selectedGlossary.id)}
                type={undefined}
                disabled={loadingRemove}
              />
            ) : (
              <LoadingSpinner />
            )}
            {/* {!loadingDelete ? (
              <Button
                translationKey="producer.control-panel.glossaries.delete"
                variant="primary"
                color="red"
                onClick={() => onDelete({ glossaryId: props.selectedGlossary.id, streamId: project.stream.id })}
                type={undefined}
                disabled={loadingDelete}
              />
            ) : (
              <LoadingSpinner />
            )} */}
          </>
        ) : null}
        </div>
      </form>
    </BaseModal>
  );
};
